/**
 * EbookReaderModal Component
 * ===========================
 * Modal viewer for PDF and EPUB files.
 * - PDF: iframe viewer with streaming
 * - EPUB: epub.js viewer (CDN loaded)
 * - MOBI: download only fallback
 */

import { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Download, 
  Maximize2, 
  Minimize2, 
  FileText, 
  BookOpen, 
  Book,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import ebookService from '@/services/ebookService';
import authService from '@/services/authService';

const FILE_ICONS = {
  '.pdf': FileText,
  '.epub': BookOpen,
  '.mobi': Book
};

export default function EbookReaderModal({ 
  isOpen, 
  onClose, 
  asset 
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const iframeRef = useRef(null);
  const epubContainerRef = useRef(null);
  const epubBookRef = useRef(null);
  
  useEffect(() => {
    if (isOpen && asset) {
      loadContent();
    }
    
    return () => {
      // Cleanup
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
      if (epubBookRef.current) {
        epubBookRef.current.destroy();
        epubBookRef.current = null;
      }
    };
  }, [isOpen, asset?.id]);
  
  const loadContent = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (asset.ext === '.pdf') {
        await loadPdf();
      } else if (asset.ext === '.epub') {
        await loadEpub();
      }
    } catch (err) {
      console.error('Error loading content:', err);
      setError(err.message || 'Failed to load file');
    } finally {
      setIsLoading(false);
    }
  };
  
  const loadPdf = async () => {
    const token = authService.getAccessToken();
    const url = ebookService.getStreamUrl(asset.id);
    
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
      throw new Error('Failed to load PDF');
    }
    
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    setPdfBlobUrl(blobUrl);
  };
  
  const loadEpub = async () => {
    // Load epub.js from CDN if not already loaded
    if (!window.ePub) {
      await loadEpubJs();
    }
    
    const token = authService.getAccessToken();
    const url = ebookService.getStreamUrl(asset.id);
    
    // Fetch EPUB as blob
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (!response.ok) {
      throw new Error('Failed to load EPUB');
    }
    
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();
    
    // Initialize epub.js
    if (epubContainerRef.current && window.ePub) {
      const book = window.ePub(arrayBuffer);
      epubBookRef.current = book;
      
      const rendition = book.renderTo(epubContainerRef.current, {
        width: '100%',
        height: '100%',
        spread: 'auto'
      });
      
      rendition.display();
    }
  };
  
  const loadEpubJs = () => {
    return new Promise((resolve, reject) => {
      if (window.ePub) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/epub.js/0.3.93/epub.min.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('Failed to load epub.js'));
      document.head.appendChild(script);
    });
  };
  
  const handleDownload = () => {
    const token = authService.getAccessToken();
    const url = ebookService.getDownloadUrl(asset.id);
    
    fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = asset.original_filename || asset.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Download failed'));
  };
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  if (!asset) return null;
  
  const FileIcon = FILE_ICONS[asset.ext] || FileText;
  const isMobi = asset.ext === '.mobi';
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`
          overflow-hidden border-0 bg-slate-950 p-0 shadow-2xl gap-0 flex flex-col [&>button]:hidden
          ${isFullscreen
            ? '!left-3 !top-3 !translate-x-0 !translate-y-0 max-w-none w-[calc(100vw-1.5rem)] h-[calc(100vh-1.5rem)] rounded-xl'
            : 'max-w-6xl w-[92vw] h-[86vh] rounded-xl'
          }
        `}
      >
        {/* Header */}
        <DialogHeader className="shrink-0 border-b border-slate-200 bg-white px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 shadow-sm">
                <FileIcon className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="truncate text-lg font-semibold text-slate-950">
                  {asset.original_filename || asset.filename}
                </DialogTitle>
                <p className="mt-1 text-xs text-slate-500">
                  {ebookService.formatFileSize(asset.size_bytes)} • {asset.ext?.replace('.', '').toUpperCase()} reader
                </p>
              </div>
            </div>
            
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownload}
                className="border-blue-600 text-slate-900 hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-1" />
                Download
              </Button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleFullscreen}
                className="text-slate-700 hover:bg-slate-100"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-4 h-4" />
                ) : (
                  <Maximize2 className="w-4 h-4" />
                )}
              </Button>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={onClose}
                className="text-slate-700 hover:bg-slate-100"
                title="Close reader"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        {/* Content */}
        <div className="min-h-0 flex-1 overflow-hidden bg-slate-900">
          {isLoading && (
            <div className="flex h-full flex-col items-center justify-center">
              <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-4" />
              <p className="text-slate-300">Loading {asset.ext?.replace('.', '').toUpperCase()}...</p>
            </div>
          )}
          
          {error && (
            <div className="flex h-full flex-col items-center justify-center">
              <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
              <p className="text-red-300 font-medium mb-2">Failed to load file</p>
              <p className="text-slate-400 text-sm mb-4">{error}</p>
              <Button onClick={handleDownload} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download Instead
              </Button>
            </div>
          )}
          
          {/* MOBI - Download Only */}
          {!isLoading && !error && isMobi && (
            <div className="flex h-full flex-col items-center justify-center">
              <Book className="w-16 h-16 text-slate-500 mb-4" />
              <p className="text-slate-200 font-medium mb-2">MOBI Preview Not Supported</p>
              <p className="text-slate-400 text-sm mb-4">
                MOBI files can be downloaded and opened with a compatible reader.
              </p>
              <Button onClick={handleDownload} className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500">
                <Download className="w-4 h-4 mr-2" />
                Download MOBI
              </Button>
            </div>
          )}
          
          {/* PDF Viewer */}
          {!isLoading && !error && asset.ext === '.pdf' && pdfBlobUrl && (
            <div className="h-full w-full bg-slate-800 p-3">
              <iframe
                ref={iframeRef}
                src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                className="h-full w-full rounded-lg border-0 bg-white shadow-xl"
                title="PDF Viewer"
              />
            </div>
          )}
          
          {/* EPUB Viewer */}
          {!isLoading && !error && asset.ext === '.epub' && (
            <div className="h-full w-full bg-slate-800 p-3">
              <div
                ref={epubContainerRef}
                className="h-full w-full rounded-lg bg-white shadow-xl"
                style={{ minHeight: '400px' }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
